// Path: ./src/app/pages/CreateBannerPage/index.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Stack,
  Switch,
} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BannerLinkType,
  BannerLocale,
  BannerPlacement,
  BannerPlatform,
  BannerStatus,
  CreateGfBannerDto,
  GfImage,
} from "@swagger/typescript-fetch-goflyer";

import { messages } from "../../../locales/messages";
import { adminApiService } from "../../../utils/api";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { useLoading } from "../../../contextApi/LoadingContext";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { useUploadOrPickImages } from "../../../hooks/useUploadOrPickImages";
import { useMerchants } from "../../../hooks/useMerchants";
import { useStores } from "../../../hooks/useStores";
import { useFlyerItemCategories } from "../../../hooks/useFlyerItemCategories";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import ImageDropZone from "../../../components/ImageDropZone";
import { extractPlaceAndCoordinates } from "../../../utils/utils";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";
import { routes } from "../../../utils/constant/navigationRoutes";
import {
  BannerRow,
  bannerToFormState,
  toBannerDate,
} from "../../../utils/banner/bannerRow";
import {
  BANNER_MAX_TARGET_RADIUS_KM,
  bannerPlacementValues,
  enumWireValues,
} from "../../../utils/banner/bannerFields";
import {
  ctaNeedsLink,
  validateBannerLink,
} from "../../../utils/banner/bannerLinkUrl";
import { useHandleSaveBanner } from "./handleSaveBanner";
import BannerContentTabs from "./BannerContentTabs";
import BannerLivePreview from "./BannerLivePreview";
import {
  AutocompleteControl,
  DateControl,
  Field,
  SelectControl,
  TextControl,
} from "../../components/FormKit/formControls";
import {
  Callout,
  ControlRow,
  FormActions,
  FormShell,
  PageShell,
  SectionBody,
  SectionBox,
  SectionHeader,
  SectionTitle,
  token,
} from "../../components/FormKit/styled";
import {
  ImageSlot,
  ImageSlotButton,
  ImageSlotOverlay,
  ImageSlotPreview,
} from "./styled";

type ImageTarget = "desktop" | "mobile";

const CreateBannerPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bannerId } = useParams<{ bannerId?: string }>();
  const authHeaders = useAuthHeaders();
  const { showLoadingScreen } = useLoading();
  const handleSaveBanner = useHandleSaveBanner();

  const [banner, setBanner] = useState<CreateGfBannerDto>({
    status: BannerStatus.Draft,
    priority: 0,
    sponsored: false,
  } as CreateGfBannerDto);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [targetingOpen, setTargetingOpen] = useState(false);
  const [googleMapUrl, setGoogleMapUrl] = useState("");

  const [desktopImage, setDesktopImage] = useState<GfImage>();
  const [mobileImage, setMobileImage] = useState<GfImage>();

  const { merchantsList, isMerchantOptionLoading, handleOnMerchantSearch } =
    useMerchants();
  const { storesList, isStoreOptionLoading, handleOnStoreSearch } = useStores();
  const { flyerItemCategories, fetchFlyerItemCategories } =
    useFlyerItemCategories();

  const isEditing = Boolean(bannerId);

  const currentUploadTargetRef = useRef<ImageTarget | undefined>();
  const [uploadingTarget, setUploadingTarget] = useState<ImageTarget>();

  const {
    handleOnImageFileSelect,
    isDragging,
    getDropZoneProps,
    isUploadingImage,
    showReorderModal,
    pendingDropFiles,
    handleReorderConfirm,
    handleReorderCancel,
  } = useUploadOrPickImages({
    onPickedImageChange: (images: GfImage[]) => {
      const image = images[images.length - 1];
      if (!image) return;
      const target: ImageTarget =
        currentUploadTargetRef.current ??
        uploadingTarget ??
        (banner.desktopImageId ? "mobile" : "desktop");

      if (target === "desktop") {
        setDesktopImage(image);
        setBanner((prev) => ({ ...prev, desktopImageId: image.id }));
      } else {
        setMobileImage(image);
        setBanner((prev) => ({ ...prev, mobileImageId: image.id }));
      }
    },
    dragAndDrop: { enabled: true, defaultPath: "banners" },
  });

  const slotDropProps = (target: ImageTarget) => {
    const base: React.HTMLAttributes<HTMLElement> = getDropZoneProps?.() ?? {};
    return {
      ...base,
      onDragEnter: (event: React.DragEvent<HTMLElement>) => {
        currentUploadTargetRef.current = target;
        base.onDragEnter?.(event);
      },
      onDrop: (event: React.DragEvent<HTMLElement>) => {
        currentUploadTargetRef.current = target;
        setUploadingTarget(target);
        base.onDrop?.(event);
      },
    };
  };

  const canInteractWithZone = (zone: ImageTarget) =>
    !(isUploadingImage && uploadingTarget && uploadingTarget !== zone);

  useEffect(() => {
    if (!isUploadingImage) {
      setUploadingTarget(undefined);
      currentUploadTargetRef.current = undefined;
    }
  }, [isUploadingImage]);

  useEffect(() => {
    fetchFlyerItemCategories();
  }, []);

  useEffect(() => {
    if (!bannerId) return;

    (async () => {
      showLoadingScreen(true);
      const fetched =
        await adminApiService.getOneBaseGfBannerAdminControllerGfBanner(
          bannerId,
          undefined,
          ["desktopImage", "mobileImage"],
          undefined,
          authHeaders,
        );
      showLoadingScreen(false);

      if (!fetched) return;

      const row = fetched as BannerRow;
      setBanner(bannerToFormState(row));
      setDesktopImage(row.desktopImage);
      setMobileImage(row.mobileImage);
      if (row.targetCenter || row.targetLocales?.length) setTargetingOpen(true);
    })();
  }, [bannerId]);

  const setField = <K extends keyof CreateGfBannerDto>(
    key: K,
    value: CreateGfBannerDto[K],
  ) => setBanner((prev) => ({ ...prev, [key]: value }));

  const handleContentFieldChange = (field: string, value: string) =>
    setBanner((prev) => ({ ...prev, [field]: value }));

  const handleImageSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    target: ImageTarget,
  ) => {
    currentUploadTargetRef.current = target;
    setUploadingTarget(target);
    handleOnImageFileSelect(event, "banners");
  };

  const handleGetCoordinatesFromUrl = () => {
    const data = extractPlaceAndCoordinates(googleMapUrl);
    if (data?.latitude === undefined || data?.longitude === undefined) {
      toastError(t(messages.UnableToExtractStoreDetails()));
      return;
    }
    setBanner((prev) => ({
      ...prev,
      targetCenter: {
        type: "Point",
        coordinates: [data.longitude as number, data.latitude as number],
      },
      targetRadiusKm: prev.targetRadiusKm ?? 25,
    }));
  };

  const setCoordinate = (index: 0 | 1, rawValue: string) => {
    const parsed = parseFloat(rawValue);
    setBanner((prev) => {
      const coordinates = [...(prev.targetCenter?.coordinates ?? [0, 0])];
      coordinates[index] = Number.isNaN(parsed) ? 0 : parsed;
      return { ...prev, targetCenter: { type: "Point", coordinates } };
    });
  };

  const clearLocationTargeting = () =>
    setBanner((prev) => ({
      ...prev,
      targetCenter: undefined,
      targetRadiusKm: undefined,
    }));

  const handleDelete = async () => {
    if (!bannerId) return;
    showLoadingScreen(true);
    const result =
      await adminApiService.deleteOneBaseGfBannerAdminControllerGfBanner(
        bannerId,
        authHeaders,
      );
    showLoadingScreen(false);
    if (result === undefined) return;

    setDeleteOpen(false);
    toastSuccess(t(messages.Successfully_Deleted()));
    navigate(routes.banner.view);
  };

  const toOptions = (values: string[]) =>
    values.map((value) => ({ label: value, value }));

  const placementOptions = useMemo(
    () => toOptions(bannerPlacementValues(String(banner.placement ?? ""))),
    [banner.placement],
  );
  const statusOptions = useMemo(
    () => toOptions(enumWireValues(BannerStatus)),
    [],
  );
  const linkTypeOptions = useMemo(
    () => toOptions(enumWireValues(BannerLinkType)),
    [],
  );
  const localeOptions = useMemo(
    () => toOptions(enumWireValues(BannerLocale)),
    [],
  );
  const platformOptions = useMemo(
    () => toOptions(enumWireValues(BannerPlatform)),
    [],
  );

  const merchantOptions = useMemo(
    () =>
      (merchantsList ?? []).map((merchant) => ({
        label: merchant.name ?? "",
        value: merchant.id ?? "",
      })),
    [merchantsList],
  );

  const storeOptions = useMemo(
    () =>
      (storesList ?? []).map((store) => ({
        label: makeStoreDisplayName(store),
        value: store.id ?? "",
      })),
    [storesList],
  );

  const categoryOptions = useMemo(
    () =>
      (flyerItemCategories ?? []).map((category) => ({
        label: category.name ?? "",
        value: category.id ?? "",
      })),
    [flyerItemCategories],
  );

  const linkError = validateBannerLink(banner.linkType, banner.linkUrl);
  const isInternalLink =
    String(banner.linkType) === String(BannerLinkType.Internal);
  const linkPairIncomplete = linkError === "pairIncomplete";
  const ctaWithoutLink = ctaNeedsLink(banner.cta, banner.linkUrl);

  const linkErrorText = (() => {
    if (ctaWithoutLink) return t(messages.banner_cta_needs_link());
    if (linkPairIncomplete && banner.linkType && !banner.linkUrl?.trim()) {
      return t(messages.banner_link_pair_required());
    }
    if (!banner.linkUrl?.trim() || !linkError) return undefined;
    if (linkError === "unsafeScheme") {
      return t(messages.banner_link_url_unsafe());
    }
    if (linkError === "internalResolvesOffSite") {
      return t(messages.banner_link_url_offsite_invalid());
    }
    return isInternalLink
      ? t(messages.banner_link_url_internal_invalid())
      : t(messages.banner_link_url_external_invalid());
  })();

  const radiusOverMax =
    (banner.targetRadiusKm ?? 0) > BANNER_MAX_TARGET_RADIUS_KM;

  const renderImageSlot = (target: ImageTarget) => {
    const image = target === "desktop" ? desktopImage : mobileImage;
    const inputId = `banner-${target}-image-input`;
    return (
      <ImageSlot
        {...slotDropProps(target)}
        $disabled={!canInteractWithZone(target)}
        $filled={Boolean(image?.src)}
        data-testid={`banner-${target}-image-slot`}
      >
        <input
          id={inputId}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => handleImageSelect(event, target)}
        />
        <ImageSlotButton
          htmlFor={inputId}
          onClick={() => {
            currentUploadTargetRef.current = target;
          }}
        >
          {image?.src ? (
            <ImageSlotPreview src={image.src} alt={image.alt ?? target} />
          ) : (
            <>
              <AddPhotoAlternateOutlinedIcon
                sx={{ fontSize: 28, color: token.color.borderStrong }}
              />
              {t(messages.Drag_your_image_here())}
            </>
          )}
        </ImageSlotButton>

        {isUploadingImage && uploadingTarget === target && (
          <ImageSlotOverlay>
            <CircularProgress size={32} sx={{ color: token.color.accent }} />
          </ImageSlotOverlay>
        )}
      </ImageSlot>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <PageShell>
        <AccountCardWrapper
          title={`${isEditing ? t(messages.Edit()) : t(messages.Create())} ${t(
            messages.Banner(),
          )}`}
        >
          <FormShell data-testid="create-banner-page">
            <SectionBox>
              <SectionHeader>
                <SectionTitle>
                  {t(messages.banner_section_identity())}
                </SectionTitle>
              </SectionHeader>

              <SectionBody>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <Field
                      id="banner-name"
                      label={t(messages.banner_name())}
                      tooltip={t(messages.banner_tip_name())}
                      required
                      help={t(messages.banner_name_helper())}
                    >
                      <TextControl
                        id="banner-name"
                        value={banner.name ?? ""}
                        onChange={(event) =>
                          setField("name", event.target.value)
                        }
                        inputProps={{ "data-testid": "banner-name-input" }}
                      />
                    </Field>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      id="banner-placement"
                      label={t(messages.banner_placement())}
                      tooltip={t(messages.banner_tip_placement())}
                      required
                    >
                      <SelectControl
                        id="banner-placement"
                        options={placementOptions}
                        value={String(banner.placement ?? "")}
                        placeholder={t(messages.banner_select_placeholder())}
                        onValueChange={(value) =>
                          setField(
                            "placement",
                            value as unknown as BannerPlacement,
                          )
                        }
                        inputProps={{
                          "data-testid": "banner-placement-select",
                        }}
                      />
                    </Field>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Field
                      id="banner-status"
                      label={t(messages.banner_status())}
                      tooltip={t(messages.banner_tip_status())}
                    >
                      <SelectControl
                        id="banner-status"
                        options={statusOptions}
                        value={String(banner.status ?? BannerStatus.Draft)}
                        onValueChange={(value) =>
                          setField("status", value as unknown as BannerStatus)
                        }
                        inputProps={{ "data-testid": "banner-status-select" }}
                      />
                    </Field>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Field
                      id="banner-priority"
                      label={t(messages.banner_priority())}
                      tooltip={t(messages.banner_tip_priority())}
                      help={t(messages.banner_priority_helper())}
                    >
                      <TextControl
                        id="banner-priority"
                        type="number"
                        value={banner.priority ?? 0}
                        onChange={(event) =>
                          setField(
                            "priority",
                            parseInt(event.target.value, 10) || 0,
                          )
                        }
                        inputProps={{
                          min: 0,
                          "data-testid": "banner-priority-input",
                        }}
                      />
                    </Field>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Field
                      id="banner-valid-start"
                      label={t(messages.banner_valid_start())}
                      tooltip={t(messages.banner_tip_valid_start())}
                      help={t(messages.banner_valid_start_helper())}
                    >
                      <DateControl
                        id="banner-valid-start"
                        value={toBannerDate(banner.validStartDate)}
                        onValueChange={(value) =>
                          setField("validStartDate", value)
                        }
                        dataTestId="banner-valid-start"
                      />
                    </Field>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Field
                      id="banner-valid-end"
                      label={t(messages.banner_valid_end())}
                      tooltip={t(messages.banner_tip_valid_end())}
                      help={t(messages.banner_valid_end_helper())}
                    >
                      <DateControl
                        id="banner-valid-end"
                        value={toBannerDate(banner.validEndDate)}
                        onValueChange={(value) =>
                          setField("validEndDate", value)
                        }
                        dataTestId="banner-valid-end"
                      />
                    </Field>
                  </Grid>
                </Grid>
              </SectionBody>
            </SectionBox>

            <SectionBox>
              <SectionHeader>
                <SectionTitle>
                  {t(messages.banner_section_content())}
                </SectionTitle>
              </SectionHeader>
              <SectionBody>
                <BannerContentTabs
                  banner={banner}
                  onFieldChange={handleContentFieldChange}
                />
              </SectionBody>
            </SectionBox>

            <SectionBox>
              <SectionHeader>
                <SectionTitle>{t(messages.banner_section_link())}</SectionTitle>
              </SectionHeader>

              <SectionBody>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={3}>
                    <Field
                      id="banner-link-type"
                      label={t(messages.banner_link_type())}
                      tooltip={t(messages.banner_tip_link_type())}
                      required={Boolean(banner.linkUrl?.trim())}
                    >
                      <SelectControl
                        id="banner-link-type"
                        options={linkTypeOptions}
                        value={String(banner.linkType ?? "")}
                        placeholder={t(messages.banner_select_placeholder())}
                        error={linkPairIncomplete && !banner.linkType}
                        onValueChange={(value) =>
                          setField(
                            "linkType",
                            value as unknown as BannerLinkType,
                          )
                        }
                        inputProps={{
                          "data-testid": "banner-link-type-select",
                        }}
                      />
                    </Field>
                  </Grid>

                  <Grid item xs={12} md={9}>
                    <Field
                      id="banner-link-url"
                      label={t(messages.banner_link_url())}
                      tooltip={t(messages.banner_tip_link_url())}
                      required={Boolean(banner.linkType) || ctaWithoutLink}
                      error={linkErrorText}
                      help={
                        isInternalLink
                          ? t(messages.banner_link_url_internal_helper())
                          : t(messages.banner_link_url_external_helper())
                      }
                    >
                      <TextControl
                        id="banner-link-url"
                        value={banner.linkUrl ?? ""}
                        error={Boolean(linkErrorText)}
                        placeholder={
                          isInternalLink
                            ? "/flyers/detail/loblaws-aug"
                            : "https://example.com"
                        }
                        onChange={(event) =>
                          setField("linkUrl", event.target.value)
                        }
                        inputProps={{ "data-testid": "banner-link-url-input" }}
                      />
                    </Field>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Field
                      id="banner-sponsored"
                      label={t(messages.banner_sponsored())}
                      tooltip={t(messages.banner_sponsored_tooltip())}
                    >
                      <ControlRow>
                        <Switch
                          id="banner-sponsored"
                          checked={Boolean(banner.sponsored)}
                          onChange={(_event, checked) =>
                            setField("sponsored", checked)
                          }
                          data-testid="banner-sponsored-checkbox"
                          sx={{
                            "& .Mui-checked": { color: token.color.accent },
                            "& .Mui-checked + .MuiSwitch-track": {
                              backgroundColor: token.color.accent,
                            },
                          }}
                        />
                      </ControlRow>
                    </Field>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Field
                      id="banner-advertiser"
                      label={t(messages.banner_advertiser_name())}
                      tooltip={t(messages.banner_tip_advertiser())}
                    >
                      <TextControl
                        id="banner-advertiser"
                        value={banner.advertiserName ?? ""}
                        onChange={(event) =>
                          setField("advertiserName", event.target.value)
                        }
                        inputProps={{
                          "data-testid": "banner-advertiser-input",
                        }}
                      />
                    </Field>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Field
                      id="banner-merchant"
                      label={t(messages.banner_merchant())}
                      tooltip={t(messages.banner_tip_merchant())}
                    >
                      <AutocompleteControl
                        id="banner-merchant"
                        multiple={false}
                        options={merchantOptions}
                        selected={banner.merchantId ? [banner.merchantId] : []}
                        loading={isMerchantOptionLoading}
                        onSearch={handleOnMerchantSearch}
                        onSelectedChange={(values) =>
                          setField("merchantId", values[0])
                        }
                        dataTestId="banner-merchant-select"
                      />
                    </Field>
                  </Grid>
                </Grid>
              </SectionBody>
            </SectionBox>

            <SectionBox>
              <SectionHeader>
                <SectionTitle>
                  {t(messages.banner_section_images())}
                </SectionTitle>
              </SectionHeader>

              <SectionBody>
                <ImageDropZone
                  uploader={{
                    getDropZoneProps,
                    isDragging,
                    showReorderModal,
                    pendingDropFiles,
                    handleReorderConfirm,
                    handleReorderCancel,
                  }}
                  label={t(messages.Drag_your_image_here())}
                  overlayContent={t(messages.Drag_your_image_here())}
                  style={{ position: "relative", width: "100%" }}
                  data-testid="banner-drag-drop-zone"
                >
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={8}>
                      <Field
                        label={t(messages.banner_desktop_image())}
                        tooltip={t(messages.banner_tip_desktop_image())}
                        required
                        help={t(messages.banner_desktop_image_helper())}
                      >
                        {renderImageSlot("desktop")}
                      </Field>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Field
                        label={t(messages.banner_mobile_image())}
                        tooltip={t(messages.banner_tip_mobile_image())}
                        help={t(messages.banner_mobile_image_helper())}
                      >
                        {renderImageSlot("mobile")}
                      </Field>
                    </Grid>
                  </Grid>
                </ImageDropZone>
              </SectionBody>
            </SectionBox>

            <SectionBox>
              <SectionHeader
                $clickable
                onClick={() => setTargetingOpen((prev) => !prev)}
                data-testid="banner-targeting-toggle"
              >
                <SectionTitle>
                  {t(messages.banner_section_targeting())}
                </SectionTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ fontSize: 12, color: token.color.textMuted }}>
                    {t(messages.banner_targeting_empty_note())}
                  </Box>
                  <IconButton size="small" aria-label="toggle targeting">
                    <ExpandMoreIcon
                      sx={{
                        transition: "transform 0.2s ease",
                        transform: targetingOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        "@media (prefers-reduced-motion: reduce)": {
                          transition: "none",
                        },
                      }}
                    />
                  </IconButton>
                </Stack>
              </SectionHeader>

              <Collapse in={targetingOpen} unmountOnExit>
                <SectionBody>
                  <Callout>{t(messages.banner_house_creative_note())}</Callout>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={8}>
                      <Field
                        id="banner-map-url"
                        label={t(messages.GoogleMapStoreURL())}
                        tooltip={t(messages.banner_tip_map_url())}
                      >
                        <TextControl
                          id="banner-map-url"
                          value={googleMapUrl}
                          placeholder="https://www.google.com/maps/place/…/@43.6532,-79.3832,14z"
                          onChange={(event) =>
                            setGoogleMapUrl(event.target.value)
                          }
                          inputProps={{ "data-testid": "banner-map-url-input" }}
                        />
                      </Field>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Field label={" "}>
                        <ControlRow>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleGetCoordinatesFromUrl}
                            sx={{
                              height: token.controlHeight,
                              textTransform: "none",
                              borderColor: token.color.borderStrong,
                              color: token.color.text,
                              "&:hover": {
                                borderColor: token.color.accent,
                                background: token.color.accentSoft,
                              },
                            }}
                            data-testid="banner-get-coordinates-button"
                          >
                            {t(messages.GetDetails())}
                          </Button>
                        </ControlRow>
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Field
                        id="banner-longitude"
                        label={t(messages.longitude())}
                        tooltip={t(messages.banner_tip_longitude())}
                      >
                        <TextControl
                          id="banner-longitude"
                          type="number"
                          value={banner.targetCenter?.coordinates?.[0] ?? ""}
                          onChange={(event) =>
                            setCoordinate(0, event.target.value)
                          }
                          inputProps={{
                            "data-testid": "banner-longitude-input",
                          }}
                        />
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Field
                        id="banner-latitude"
                        label={t(messages.latitude())}
                        tooltip={t(messages.banner_tip_latitude())}
                      >
                        <TextControl
                          id="banner-latitude"
                          type="number"
                          value={banner.targetCenter?.coordinates?.[1] ?? ""}
                          onChange={(event) =>
                            setCoordinate(1, event.target.value)
                          }
                          inputProps={{
                            "data-testid": "banner-latitude-input",
                          }}
                        />
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Field
                        id="banner-radius"
                        label={t(messages.banner_target_radius())}
                        tooltip={t(messages.banner_tip_radius())}
                        required={Boolean(banner.targetCenter)}
                        error={
                          radiusOverMax
                            ? t(messages.banner_radius_too_large())
                            : undefined
                        }
                      >
                        <TextControl
                          id="banner-radius"
                          type="number"
                          value={banner.targetRadiusKm ?? ""}
                          error={radiusOverMax}
                          onChange={(event) => {
                            const parsed = parseFloat(event.target.value);
                            setField(
                              "targetRadiusKm",
                              Number.isNaN(parsed) ? undefined : parsed,
                            );
                          }}
                          inputProps={{
                            min: 0,
                            max: BANNER_MAX_TARGET_RADIUS_KM,
                            "data-testid": "banner-radius-input",
                          }}
                        />
                      </Field>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Field
                        id="banner-locales"
                        label={t(messages.banner_target_locales())}
                        tooltip={t(messages.banner_tip_locales())}
                      >
                        <AutocompleteControl
                          id="banner-locales"
                          options={localeOptions}
                          selected={(banner.targetLocales ?? []).map(String)}
                          onSelectedChange={(values) =>
                            setField(
                              "targetLocales",
                              values as unknown as BannerLocale[],
                            )
                          }
                          dataTestId="banner-locales-select"
                        />
                      </Field>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Field
                        id="banner-platforms"
                        label={t(messages.banner_target_platforms())}
                        tooltip={t(messages.banner_tip_platforms())}
                      >
                        <AutocompleteControl
                          id="banner-platforms"
                          options={platformOptions}
                          selected={(banner.targetPlatforms ?? []).map(String)}
                          onSelectedChange={(values) =>
                            setField(
                              "targetPlatforms",
                              values as unknown as BannerPlatform[],
                            )
                          }
                          dataTestId="banner-platforms-select"
                        />
                      </Field>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Field
                        id="banner-stores"
                        label={t(messages.banner_target_stores())}
                        tooltip={t(messages.banner_tip_stores())}
                      >
                        <AutocompleteControl
                          id="banner-stores"
                          options={storeOptions}
                          selected={banner.targetStoreIds ?? []}
                          loading={isStoreOptionLoading}
                          onSearch={handleOnStoreSearch}
                          onSelectedChange={(values) =>
                            setField("targetStoreIds", values)
                          }
                          dataTestId="banner-stores-select"
                        />
                      </Field>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Field
                        id="banner-categories"
                        label={t(messages.banner_target_categories())}
                        tooltip={t(messages.banner_tip_categories())}
                      >
                        <AutocompleteControl
                          id="banner-categories"
                          options={categoryOptions}
                          selected={banner.targetCategoryIds ?? []}
                          onSelectedChange={(values) =>
                            setField("targetCategoryIds", values)
                          }
                          dataTestId="banner-categories-select"
                        />
                      </Field>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        size="small"
                        onClick={clearLocationTargeting}
                        sx={{
                          textTransform: "none",
                          color: token.color.textMuted,
                          "&:hover": { color: token.color.danger },
                        }}
                        data-testid="banner-clear-targeting-button"
                      >
                        {t(messages.banner_clear_targeting())}
                      </Button>
                    </Grid>
                  </Grid>
                </SectionBody>
              </Collapse>
            </SectionBox>

            <SectionBox>
              <SectionHeader>
                <SectionTitle>
                  {t(messages.banner_section_preview())}
                </SectionTitle>
              </SectionHeader>
              <SectionBody>
                <BannerLivePreview
                  banner={banner}
                  desktopImage={desktopImage}
                  mobileImage={mobileImage}
                />
              </SectionBody>
            </SectionBox>

            <FormActions>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {isEditing && (
                  <PermissionComponentWrapper
                    requiredPermission={["delete"]}
                    requiredPermissionGroup={["banner"]}
                  >
                    <Button
                      onClick={() => setDeleteOpen(true)}
                      sx={{
                        textTransform: "none",
                        color: token.color.danger,
                        "&:hover": { background: "#fdecec" },
                      }}
                      data-testid="banner-delete-button"
                    >
                      {t(messages.Delete())}
                    </Button>
                  </PermissionComponentWrapper>
                )}
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  variant="text"
                  onClick={() => navigate(-1)}
                  sx={{
                    textTransform: "none",
                    color: token.color.textMuted,
                  }}
                  data-testid="banner-cancel-button"
                >
                  {t(messages.Cancel())}
                </Button>
                <PermissionComponentWrapper
                  requiredPermission={[isEditing ? "edit" : "create"]}
                  requiredPermissionGroup={["banner"]}
                >
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => handleSaveBanner(banner, bannerId)}
                    sx={{
                      minWidth: 132,
                      height: token.controlHeight,
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: token.radius.sm,
                      backgroundColor: token.color.accent,
                      "&:hover": { backgroundColor: token.color.accentHover },
                    }}
                    data-testid="banner-save-button"
                  >
                    {isEditing ? t(messages.Update()) : t(messages.Save())}
                  </Button>
                </PermissionComponentWrapper>
              </Stack>
            </FormActions>
          </FormShell>
        </AccountCardWrapper>

        <MuiConfirmationModal
          open={deleteOpen}
          handleCancel={() => setDeleteOpen(false)}
          handleConfirm={handleDelete}
          title={t(messages.Delete_Confirmation())}
          description={`${t(
            messages.Are_you_sure_you_want_to_delete_These(),
          )}? ${t(messages.This_action_cannot_be_undone())}.`}
        />
      </PageShell>
    </LocalizationProvider>
  );
};

export default CreateBannerPage;
